import { Currency, IPrice, IVipPrice } from '@/api/types/public';
import { ColorItem } from '@/app/_lens-components/types';
import { handleColorItemPriceInfo } from '@/app/_lens-components/utils';
import { useStore } from '@/providers/app-store-provider';
import { add } from '@/utils/math';
import { formatPriceNumberWithSymbol } from '@/utils/price';
import React, { useEffect, useMemo, useState } from 'react';

interface LensInfo {
  lens_type_price?: IPrice[];
  lens_index_price?: IPrice[];
}

interface IVipPriceWithLens extends IVipPrice {
  lens_info?: LensInfo;
  lens_type_price?: IPrice[];
  index_price?: IPrice[];
}

export default function Price({
  renderPrice,
  currency,
  currencyName,
  price,
  sellPrice,
  priceObject,
  priceType = 'product',
  isNonPres = false,
  hasIndexPrice = false,
}: {
  renderPrice: (price?: string, sellPrice?: string) => React.ReactNode;
  currency?: Currency;
  price?: string;
  sellPrice?: string;
  priceObject?: IVipPriceWithLens;
  currencyName?: string;
  priceType?: string; // default: product 商品价格, lens_type: 镜片类型价格, lens_index: 镜片折射率价格, lens_process: 配镜流程
  isNonPres?: boolean; // 是否是非处方镜片
  hasIndexPrice?: boolean; // 配镜流程类型中是否包含折射率价格
}) {
  const currencyList = useStore((state) => state.currencyList);
  const currencyStore = useStore((state) => state.currency);
  const userInfo = useStore((state) => state.userInfo);
  const token = useStore((state) => state.token);
  const [nowPrice, setNowPrice] = useState<string>(price);
  const [nowSellPrice, setNowSellPrice] = useState<string>(sellPrice);

  const nowCurrency = useMemo(() => {
    return currency || currencyList.find((item) => item.name === currencyName) || currencyStore;
  }, [currency, currencyList, currencyName, currencyStore]);

  useEffect(() => {
    if (priceObject) {
      let nowPriceInfo: IPrice;
      if (priceType === 'product') {
        if (token && userInfo.id && userInfo.is_member === 1) {
          nowPriceInfo = (priceObject[`price_info_v${userInfo.member_level}`] as IPrice[]).find(
            (item) => item.currency === nowCurrency.name,
          );
        } else {
          nowPriceInfo = priceObject.price_info.find((item) => item.currency === nowCurrency.name);
        }
      } else if (priceType === 'lens') {
        const lens_price = priceObject?.lens_price as IPrice[];
        const coating_price = priceObject?.coating_price as IPrice[];
        // nowPriceInfo = lens_price?.find((item) => item.currency === nowCurrency.name);
        nowPriceInfo = {
          currency: nowCurrency.name,
          price: Number(add(Number(lens_price), Number(coating_price))).toFixed(2),
          sell_price: Number(add(Number(lens_price), Number(coating_price))).toFixed(2),
        };
      } else if (priceType === 'lens_type') {
        // const lens_type_price = priceObject?.lens_type_price as IPrice[];
        // nowPriceInfo = lens_type_price?.find((item) => item.currency === nowCurrency.name);
        if (isNonPres) {
          const priceItem = priceObject?.lens_type_price?.find(
            (item) => item.currency === nowCurrency.name,
          );
          const indexPriceItem = priceObject?.index_price?.find(
            (item) => item.currency === nowCurrency.name,
          );
          nowPriceInfo = {
            currency: nowCurrency.name,
            price: Number(add(Number(priceItem?.price), Number(indexPriceItem?.price))).toFixed(2),
            sell_price: Number(
              add(Number(priceItem?.sell_price), Number(indexPriceItem?.sell_price)),
            ).toFixed(2),
          };
        } else {
          nowPriceInfo = priceObject?.lens_type_price?.find(
            (item) => item.currency === nowCurrency.name,
          );
        }
      } else if (priceType === 'lens_index') {
        const lens_index_price = priceObject?.index_price as IPrice[];
        nowPriceInfo = lens_index_price?.find((item) => item.currency === nowCurrency.name);
      } else if (priceType === 'lens_coating') {
        nowPriceInfo = priceObject?.price_info?.find((item) => item.currency === nowCurrency.name);
      } else if (priceType === 'lens_process') {
        nowPriceInfo = handleColorItemPriceInfo(
          priceObject as unknown as ColorItem,
          hasIndexPrice,
          nowCurrency,
          userInfo,
        );
      }
      setNowPrice(nowPriceInfo?.price);
      setNowSellPrice(nowPriceInfo?.sell_price);
    }
  }, [priceObject, nowCurrency, token, userInfo]);
  return renderPrice(
    nowPrice !== nowSellPrice && nowPrice
      ? formatPriceNumberWithSymbol(nowPrice, nowCurrency)
      : undefined,
    nowSellPrice ? formatPriceNumberWithSymbol(nowSellPrice, nowCurrency) : undefined,
  );
}
